import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, LayoutDashboard } from 'lucide-react';
import Reveal from './Reveal';
import ScanRing from './ScanRing';
import WhatsAppCta from './WhatsAppCta';
import { BRAND_NAME } from '../utils/brand';
import { useReferralCta } from '../hooks/useReferralCta';
import { useAuthStatus } from '../hooks/useAuthStatus';
import { trackCtaClick } from '../utils/analytics';

//
// Page-end CTA. Brand-correct, spec-compliant copy: doctor-led, AI-assisted,
// no "facilitation platform" or "GLP-1/GIP treatment" claims. Soft scan ring
// replaces the old blur blobs; everything reveals on first view.
//
export default function CTASection() {
  const { ctaUrl } = useReferralCta();
  const authed = useAuthStatus();

  function onPrimary() {
    trackCtaClick({
      page: 'cta_section',
      button_label: authed ? 'Open my dashboard' : 'Am I Qualified?',
      destination: authed ? 'portal_dashboard' : '/intake',
    });
  }

  return (
    <section className="bg-cta-radial">
      <div className="container-x py-20 sm:py-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl bg-ink-900 text-white px-8 py-14 sm:px-14 sm:py-20">
            <ScanRing className="absolute -right-1/4 -top-1/4 w-[36rem] h-[36rem]" tone="teal" />
            <ScanRing className="absolute -left-1/4 -bottom-1/4 w-[36rem] h-[36rem]" tone="navy" />

            <div className="relative max-w-2xl">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium tracking-wide uppercase bg-white/10 text-brand-200 border border-white/10">
                Ready when you are
              </span>
              <h2 className="h-section mt-5 text-white">
                Take your free AI-assisted assessment.
              </h2>
              <p className="lead mt-4 text-white/80">

 A few private questions help us understand whether you may be suitable for a doctor-guided weight management programme. Your answers help us match you with a licensed clinic for the next step.
              </p>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                {authed ? (
                  <a
                    href={ctaUrl}
                    onClick={onPrimary}
                    className="inline-flex items-center gap-2 font-semibold rounded-full px-6 py-3.5 text-sm bg-white text-ink-900 hover:bg-ink-100 transition-colors"
                  >
                    <LayoutDashboard size={16} /> Open my dashboard
                  </a>
                ) : (
                  <Link
                    to="/intake"
                    onClick={onPrimary}
                    className="inline-flex items-center gap-2 font-semibold rounded-full px-6 py-3.5 text-sm bg-brand-600 text-white hover:bg-brand-700 transition-colors shadow-card"
                  >
                    Am I Qualified? <ArrowRight size={16} />
                  </Link>
                )}
                <WhatsAppCta
                  sourcePage="cta_section"
                  label={`Talk to ${BRAND_NAME} on WhatsApp`}
                  variant="ghost"
                  className="!border-white/25 !text-white hover:!bg-white/10"
                />
              </div>

              <p className="mt-6 text-xs text-white/55 max-w-lg">
                {BRAND_NAME} is a healthcare technology and care coordination platform. Assessments support clinic matching and booking. Doctor consultation, diagnosis and treatment decisions are made by independent licensed healthcare providers. Programme suitability is subject to doctor consultation.

              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
